import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { firstName as firstNameOf } from '../lib/auth'
import type { Stats, Tree, User, FamilyEvent } from '../lib/types'
import FindYourPlace from '../components/FindYourPlace'
import GettingStarted from '../components/GettingStarted'
import NamePrompt from '../components/NamePrompt'
import PushPrompt from '../components/PushPrompt'
import { useVeil } from '../lib/useVeil'
import { maskName } from '../lib/security'
import { useI18n, segments } from '../lib/i18n'

const ACCENTS: Record<string, string> = {
  gold: 'from-gold-500/30 to-gold-600/5 border-gold-500/20',
  violet: 'from-rootv-500/30 to-rootv-600/5 border-rootv-500/20',
  teal: 'from-teal-500/30 to-teal-600/5 border-teal-500/20',
  rose: 'from-rose-500/30 to-rose-600/5 border-rose-500/20',
}

function StatTile({ label, value, sub, icon, delay }: { label: string; value: number | string; sub?: string; icon: string; delay: string }) {
  return (
    <div className={`card-glass shimmer p-5 ${delay}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">{label}</p>
          <p className="font-display text-3xl font-bold text-gradient-gold">{value}</p>
          {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
        </div>
        <span className="text-2xl opacity-80">{icon}</span>
      </div>
    </div>
  )
}

interface Moment { icon: string; label: string; sub: string; today: boolean }

/** birthdays of the living, remembrance days, and meet-ups in the next 14 days */
function useFamilyMoments(veil: boolean) {
  const { t, locale } = useI18n()
  const [moments, setMoments] = useState<Moment[]>([])
  useEffect(() => {
    const now = new Date()
    const from = now.toISOString().slice(0, 10)
    const toD = new Date(now.getTime() + 14 * 86400000)
    const to = toD.toISOString().slice(0, 10) + 'T23:59'
    api.listEvents(from, to).then(({ events, persons }) => {
      const out: Moment[] = []
      const todayStr = from
      const mmddIn = (dateStr: string) => {
        // next occurrence of MM-DD within the window
        const mmdd = dateStr.slice(5, 10)
        for (let d = 0; d <= 14; d++) {
          const day = new Date(now.getTime() + d * 86400000)
          if (day.toISOString().slice(5, 10) === mmdd) return day.toISOString().slice(0, 10)
        }
        return null
      }
      for (const p of persons) {
        const name = maskName(p, veil)
        if (p.birth_date && p.is_living) {
          const hit = mmddIn(p.birth_date)
          if (hit) {
            const birthYear = parseInt(p.birth_date.slice(0, 4))
            // year 0000 = redacted (shared-tree living person): show the day, not the age
            const label = birthYear > 0 ? t('dash.turns', { name, age: parseInt(hit.slice(0, 4)) - birthYear }) : t('dash.birthday', { name })
            out.push({ icon: '🎂', label, sub: `${hit} · ${p.tree_name}`, today: hit === todayStr })
          }
        }
        if (p.death_date) {
          const hit = mmddIn(p.death_date)
          if (hit) out.push({ icon: '🕯️', label: t('dash.remembering', { name }), sub: `${hit} · ${p.tree_name}`, today: hit === todayStr })
        }
      }
      for (const e of events as FamilyEvent[]) {
        out.push({ icon: '🫂', label: e.title, sub: `${e.start_at.slice(0, 10)} · ${e.tree_name}`, today: e.start_at.slice(0, 10) === todayStr })
      }
      out.sort((a, b) => a.sub.localeCompare(b.sub))
      setMoments(out.slice(0, 6))
    }).catch(() => {})
  }, [veil, locale]) // eslint-disable-line react-hooks/exhaustive-deps
  return moments
}

function SeedCeremony({ onDone }: { onDone: (treeId: string) => void }) {
  const [step, setStep] = useState(0)
  const [treeName, setTreeName] = useState('')
  const [myName, setMyName] = useState('')
  const [myBirth, setMyBirth] = useState('')
  const [mother, setMother] = useState('')
  const [father, setFather] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const finish = async () => {
    setBusy(true); setErr('')
    try {
      const tree = await api.createTree(treeName.trim() || 'My Family')
      const [first, ...rest] = myName.trim().split(' ')
      const me = await api.createPerson({
        tree_id: tree.id, first_name: first || 'Me', last_name: rest.join(' '),
        birth_date: myBirth || undefined,
      })
      // save the name to the ACCOUNT too — Find-your-place self-matching and claim
      // suggestions key off user.name, which otherwise stays empty forever
      if (myName.trim()) {
        api.updateProfile(myName.trim()).then(u => {
          import('../lib/auth').then(({ setCachedUser }) => setCachedUser(u))
        }).catch(() => {})
      }
      for (const [parentName, gender] of [[mother, 'female'], [father, 'male']] as const) {
        if (!parentName.trim()) continue
        const [pf, ...pr] = parentName.trim().split(' ')
        await api.createPerson({
          tree_id: tree.id, first_name: pf, last_name: pr.join(' '), gender,
          relation: { type: 'parent-of', personId: me.id },
        })
      }
      onDone(tree.id)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Something went wrong'
      setErr(msg.includes('403')
        ? 'Your session predates your account approval. Please sign out and back in, then try again.'
        : msg.replace(/^API \d+: /, '').replace(/[{}"]|error:/g, '') || 'Could not create your constellation — try again.')
    } finally { setBusy(false) }
  }

  const steps = [
    <div key="0">
      <div className="text-5xl mb-4 text-center float-slow">🌱</div>
      <h3 className="font-display text-xl font-bold text-slate-100 text-center mb-2">Name your constellation</h3>
      <p className="text-xs text-slate-500 text-center mb-5">Every dynasty begins with a name for its sky.</p>
      <input autoFocus placeholder="e.g. The Ashford Line" value={treeName}
        onChange={e => setTreeName(e.target.value)} className="input-dark text-center" />
    </div>,
    <div key="1">
      <div className="text-5xl mb-4 text-center float-slow">✨</div>
      <h3 className="font-display text-xl font-bold text-slate-100 text-center mb-2">Place yourself among the stars</h3>
      <p className="text-xs text-slate-500 text-center mb-5">You are the first light — generations grow upward from you.</p>
      <input autoFocus placeholder="Your full name" value={myName}
        onChange={e => setMyName(e.target.value)} className="input-dark mb-3" />
      <label className="text-[10px] text-slate-500 uppercase tracking-wide">Born (optional)</label>
      <input type="date" value={myBirth} onChange={e => setMyBirth(e.target.value)} className="input-dark" />
    </div>,
    <div key="2">
      <div className="text-5xl mb-4 text-center float-slow">🌌</div>
      <h3 className="font-display text-xl font-bold text-slate-100 text-center mb-2">Summon the first ancestors</h3>
      <p className="text-xs text-slate-500 text-center mb-5">Optional — you can always add them later, or just tell Sage.</p>
      <input autoFocus placeholder="Mother's name (optional)" value={mother}
        onChange={e => setMother(e.target.value)} className="input-dark mb-3" />
      <input placeholder="Father's name (optional)" value={father}
        onChange={e => setFather(e.target.value)} className="input-dark" />
    </div>,
  ]

  const canNext = step === 0 ? treeName.trim().length > 0 : step === 1 ? myName.trim().length > 0 : true

  return (
    <div className="card-glass p-8 max-w-md mx-auto">
      <div className="flex justify-center gap-1.5 mb-6">
        {[0, 1, 2].map(i => (
          <span key={i} className={`h-1.5 rounded-full transition-all ${i <= step ? 'w-8 bg-gold-400' : 'w-4 bg-white/10'}`} />
        ))}
      </div>
      {steps[step]}
      <div className="flex gap-3 mt-7">
        {step > 0 && <button onClick={() => setStep(s => s - 1)} className="btn-ghost flex-none">←</button>}
        {step < 2 ? (
          <button onClick={() => setStep(s => s + 1)} disabled={!canNext} className="btn-gold btn-shine flex-1">Continue</button>
        ) : (
          <button onClick={finish} disabled={busy || !myName.trim()} className="btn-gold btn-shine flex-1">
            {busy ? 'Igniting…' : '✳ Ignite the constellation'}
          </button>
        )}
      </div>
      {err && <p className="text-sm text-red-400 mt-4 text-center">{err}</p>}
    </div>
  )
}

export default function Dashboard({ user, onUpdate }: { user: User; onUpdate: (u: User) => void }) {
  const [stats, setStats] = useState<Stats | null>(null)
  const [trees, setTrees] = useState<Tree[]>([])
  const [loading, setLoading] = useState(true)
  const veil = useVeil()
  const { t } = useI18n()
  const moments = useFamilyMoments(veil)
  const [seedSkipped, setSeedSkipped] = useState(() => localStorage.getItem('fa_seed_skipped') === '1')
  const navigate = useNavigate()

  const [orgCount, setOrgCount] = useState(0)
  useEffect(() => {
    Promise.all([api.getStats(), api.listTrees()])
      .then(([s, t]) => { setStats(s); setTrees(t) })
      .finally(() => setLoading(false))
    api.listOrgs().then(o => setOrgCount(o.length)).catch(() => {})
  }, [])

  const generations = useMemo(
    () => stats && stats.persons > 0 ? Math.max(1, Math.round(Math.log2(stats.persons + 1))) : 0,
    [stats]
  )

  if (loading) {
    return <div className="flex items-center justify-center py-32">
      <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-gold-400" />
    </div>
  }

  const firstName = firstNameOf(user)

  // ── First run: the Seed Ceremony ──
  if (trees.length === 0 && !seedSkipped) {
    return (
      <div className="relative max-w-3xl mx-auto px-4 py-16 overflow-hidden">
        <div className="aurora aurora-gold aurora-drift w-[380px] h-[380px] -top-24 -right-16 opacity-20" />
        <div className="aurora aurora-violet aurora-drift-2 w-[320px] h-[320px] bottom-0 -left-20 opacity-15" />
        <p className="relative text-gold-400 tracking-[0.3em] text-[11px] font-semibold uppercase text-center mb-2 fade-up">{t('dash.seedEyebrow')}</p>
        <h1 className="relative font-display text-3xl sm:text-4xl font-bold text-slate-100 text-center mb-10 fade-up-1">
          {segments(t('dash.seedWelcome', { name: firstName })).map((sg, i) =>
            sg.tag === 'gold' ? <span key={i} className="text-gradient-gold">{sg.text}</span> : <span key={i}>{sg.text}</span>)}
        </h1>
        <div className="relative fade-up-2">
          <SeedCeremony onDone={treeId => navigate(`/trees/${treeId}`)} />
        </div>
        <p className="relative text-center mt-6 fade-up-2">
          <button onClick={() => { localStorage.setItem('fa_seed_skipped', '1'); setSeedSkipped(true) }}
            className="text-xs text-slate-500 hover:text-slate-300 underline underline-offset-4">
            {t('dash.skipSeed')}
          </button>
        </p>
      </div>
    )
  }

  return (
    <div className="relative max-w-7xl mx-auto px-4 py-8 overflow-hidden">
      <div className="aurora aurora-gold aurora-drift w-[380px] h-[380px] -top-32 -right-20 opacity-15" />
      <div className="aurora aurora-violet aurora-drift-2 w-[300px] h-[300px] top-64 -left-24 opacity-10" />

      {/* Greeting */}
      <div className="relative mb-8 fade-up">
        <p className="text-gold-400 tracking-[0.25em] text-[11px] font-semibold uppercase mb-1">{t('dash.eyebrow')}</p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-100">
          {segments(t('dash.welcomeBack', { name: firstName })).map((sg, i) =>
            sg.tag === 'gold' ? <span key={i} className="text-gradient-gold">{sg.text}</span> : <span key={i}>{sg.text}</span>)}
        </h1>
        {stats?.oldest_ancestor && (
          <p className="text-sm text-slate-500 mt-2">
            {segments(t('dash.deepestRoot', { name: maskName(stats.oldest_ancestor, veil), date: (veil ? stats.oldest_ancestor.birth_date?.slice(0, 4) : stats.oldest_ancestor.birth_date) || '' })).map((sg, i) =>
              sg.tag === 'b' ? <span key={i} className="text-slate-300">{sg.text}</span> : <span key={i}>{sg.text}</span>)}
          </p>
        )}
      </div>

      {trees.length === 0 && seedSkipped && (
        <div className="relative card-glass p-5 mb-6 border-gold-500/25 fade-up flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-100">🌱 {t('dash.emptySkyTitle')}</h2>
            <p className="text-xs text-slate-500">{t('dash.emptySkyBody')}</p>
          </div>
          <button onClick={() => { localStorage.removeItem('fa_seed_skipped'); setSeedSkipped(false) }} className="btn-gold !py-2 text-sm">{t('dash.beginSeed')}</button>
        </div>
      )}

      {/* One ask at a time (v0.20.1, D-121): a returning relative meeting five
          simultaneous chores closes the tab. Identity first — it unlocks the most. */}
      {!user.name?.trim()
        ? <NamePrompt user={user} onUpdate={onUpdate} />
        : <PushPrompt />}

      <GettingStarted items={[
        { label: t('dash.gs.tree'), done: trees.length > 0, hint: t('dash.gs.treeHint'), onClick: () => { localStorage.removeItem('fa_seed_skipped'); setSeedSkipped(false); navigate('/dashboard') } },
        { label: t('dash.gs.record'), done: (stats?.persons || 0) >= 3, hint: t('dash.gs.recordHint'), onClick: () => navigate('/trees') },
        { label: t('dash.gs.sage'), done: localStorage.getItem('fa_sage_used') === '1', hint: t('dash.gs.sageHint'), onClick: () => window.dispatchEvent(new CustomEvent('sage:open')) },
        { label: t('dash.gs.circle'), done: orgCount > 0, hint: t('dash.gs.circleHint'), onClick: () => navigate('/circles') },
        { label: t('dash.gs.tour'), done: !!user.tour_done || localStorage.getItem('fa_tour_done') === '1', hint: t('dash.gs.tourHint'), onClick: () => window.dispatchEvent(new CustomEvent('tour:start')) },
      ]} />

      {!user.placed && <FindYourPlace />}

      {/* Today in the family */}
      {moments.length > 0 && (
        <div className="relative card-glass p-5 mb-6 border-gold-500/20 fade-up">
          <h2 className="font-display text-lg font-semibold text-slate-100 mb-3">🕯️ {t('dash.momentsTitle')}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {moments.map((m, i) => (
              <div key={i} className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border ${
                m.today ? 'bg-gold-500/10 border-gold-500/35' : 'bg-white/[0.03] border-white/[0.06]'}`}>
                <span className="text-xl">{m.icon}</span>
                <div className="min-w-0">
                  <p className="text-sm text-slate-200 truncate">{m.label}{m.today && <span className="text-gold-300 text-[10px] ms-1.5 font-semibold">{t('dash.todayBadge')}</span>}</p>
                  <p className="text-[11px] text-slate-500 truncate">{m.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats constellation */}
      <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatTile label={t('dash.statTrees')} value={stats?.trees || 0} icon="🌳" delay="fade-up" sub={t('dash.statTreesSub')} />
        <StatTile label={t('dash.statSouls')} value={stats?.persons || 0} icon="✨" delay="fade-up-1" sub={t('dash.statSoulsSub', { count: stats?.living || 0 })} />
        <StatTile label={t('dash.statUnions')} value={stats?.unions || 0} icon="💍" delay="fade-up-2" sub={t('dash.statUnionsSub')} />
        <StatTile label={t('dash.statLines')} value={stats?.bloodlines || 0} icon="🩸" delay="fade-up-3" sub={t('dash.statLinesSub', { count: generations })} />
      </div>

      {/* Trees + activity */}
      <div className="relative grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-semibold text-slate-100">{t('dash.constellations')}</h2>
            <Link to="/trees" className="text-sm text-gold-400 hover:text-gold-300">{t('dash.manageAll')}</Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {trees.slice(0, 4).map((tr, i) => (
              <Link key={tr.id} to={`/trees/${tr.id}`}
                className={`card-glass bg-gradient-to-br ${ACCENTS[tr.accent] || ACCENTS.gold} p-5 hover:scale-[1.02] transition-transform fade-up-${Math.min(i, 3) || ''}`}>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-display font-semibold text-lg text-slate-100 truncate">{tr.name}</h3>
                  {tr.my_role && tr.my_role !== 'owner' && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-rootv-500/15 text-rootv-400 border border-rootv-500/25 flex-shrink-0">{tr.my_role}</span>
                  )}
                </div>
                {tr.description && <p className="text-xs text-slate-400 mb-3 line-clamp-2">{tr.description}</p>}
                <div className="flex gap-4 text-xs text-slate-400">
                  <span>✨ {t('tree.souls', { count: tr.person_count || 0 })}</span>
                  <span>🔗 {t('tree.bonds', { count: tr.relationship_count || 0 })}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-display text-xl font-semibold text-slate-100 mb-4">{t('dash.echoes')}</h2>
          <div className="card-glass p-4">
            {(stats?.recent?.length || 0) === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">{t('dash.echoesEmpty')}</p>
            ) : (
              <div className="space-y-1">
                {stats!.recent.map(r => (
                  <Link key={r.id} to={`/trees/${r.tree_id}?person=${r.id}`}
                    className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5 transition-colors">
                    <span className="w-2 h-2 rounded-full bg-gold-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-200 truncate">{maskName(r, veil)}</p>
                      <p className="text-[11px] text-slate-500">{r.tree_name}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="card-glass p-5 mt-4 border-gold-500/20">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">🧙</span>
              <h3 className="font-display font-semibold text-slate-100">{t('dash.sageAwaits')}</h3>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              {segments(t('dash.sageBlurb')).map((sg, i) =>
                sg.tag === 'kbd' ? <kbd key={i} className="text-[10px] border border-white/15 rounded px-1">{sg.text}</kbd> : <span key={i}>{sg.text}</span>)}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
